<template>
  <WidgetCadre
    titre="Accès rapides"
    v-bind="apparence"
    :reglable="reglable"
    @retirer="$emit('retirer')"
  >
    <nav class="raccourcis">
      <button
        v-for="tuile in tuilesVisibles"
        :key="tuile.route"
        class="raccourci surface"
        type="button"
        @click="router.push(tuile.route)"
      >
        <span class="raccourci__glyphe">
          <Icon
            :icon="tuile.icone"
            :width="16"
          />
        </span>
        <span class="raccourci__libelle">{{ tuile.libelle }}</span>
      </button>

      <p
        v-if="!tuilesVisibles.length"
        class="etiquette"
      >
        Aucun écran ne vous est ouvert depuis ce raccourci.
      </p>
    </nav>
  </WidgetCadre>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { Icon } from '@iconify/vue'
import { checkPerms } from '@/utils/checkPerms'
import { P } from '@/config/permissions'
import WidgetCadre from './WidgetCadre.vue'
import { apparenceDe } from './apparence'

/**
 * Les raccourcis de l'ancien accueil, devenus un widget parmi les autres.
 *
 * Des tuiles compactes, une pastille de couleur par écran : assez de couleur
 * pour qu'on retrouve un écran d'un coup d'œil, assez petites pour que onze
 * entrées tiennent sur deux rangs sans en tronquer un troisième.
 *
 * Les tuiles sont déclarées, pas écrites une à une : douze blocs `v-if`
 * copiés-collés, c'est douze endroits où oublier un droit.
 */

defineProps({
    donnees: { type: [Object, Array], default: null },
    chargement: { type: Boolean, default: false },
    panne: { type: Boolean, default: false },
    reglable: { type: Boolean, default: false },
})

defineEmits(['retirer', 'recharger'])

const apparence = apparenceDe('raccourcis')

const router = useRouter()

const TUILES = [
    { libelle: 'Comptes', route: '/accounts', icone: 'mdi:account-multiple-outline', droits: [P.COMPTES.BROWSE] },
    { libelle: 'Filtrage avancé', route: '/comptes/listes', icone: 'mdi:filter-variant', droits: [P.COMPTES.BROWSE] },
    { libelle: 'Carte des comptes', route: '/comptes/carte', icone: 'mdi:map-marker-outline', droits: [P.COMPTES.BROWSE] },
    { libelle: 'Prélèvements', route: '/automaticdebits/dash', icone: 'mdi:credit-card-outline', droits: [P.PRLV.BROWSE] },
    // Deux droits en OU, comme la route : sous le seul `browse renewable
    // services`, un porteur de `manage services` avait l'écran sans la tuile.
    { libelle: 'Renouvellements', route: '/renouvelement', icone: 'mdi:autorenew', droits: [P.SERVICES.BROWSE_RENEWABLE, P.SERVICES.MANAGE] },
    { libelle: 'Statistiques', route: '/stats/mobileROI', icone: 'mdi:chart-box-outline', droits: [P.MISC.BROWSE_STATS] },
    { libelle: 'Comparaison du CA', route: '/compareCA', icone: 'mdi:chart-line', droits: [P.COMMERCIAL.BROWSE] },
    { libelle: 'Numéros fixes', route: '/gestionNumFixe', icone: 'mdi:phone-outline', droits: [P.GESTION_NUM_FIXE.BROWSE] },
    { libelle: 'Grilles tarifaires', route: '/tariffmanagement/tm_main', icone: 'mdi:table', droits: [P.PRODUCTS.MANAGE] },
    { libelle: 'Utilisateurs', route: '/administrator/users', icone: 'mdi:account-cog-outline', droits: [P.USERS.BROWSE] },
    // `/administrator/dash` n'existe pas : l'écran est monté sous `permissions`.
    { libelle: 'Droits et rôles', route: '/administrator/permissions', icone: 'mdi:shield-key-outline', droits: [P.ADMIN.MANAGE_ROLES] },
]

const tuilesVisibles = computed(() => TUILES.filter(tuile => checkPerms(tuile.droits)))
</script>

<style lang="scss" scoped>
.raccourcis {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 8px;
}

.raccourci {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px 7px 7px;
  border: 1px solid var(--el-border-color-lighter);
  cursor: pointer;
  text-align: left;

  &:hover &__glyphe {
    background: color-mix(in srgb, var(--accent) 14%, transparent);
    color: var(--accent);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }

  /* Monochrome : la tuile se reconnaît à son dessin et à son nom, pas à sa
     couleur — onze teintes côte à côte n'en distinguaient aucune. La teinte du
     widget ne revient qu'au survol, sur le glyphe. */
  &__glyphe {
    display: grid;
    place-items: center;
    width: 26px;
    height: 26px;
    border-radius: 8px;
    background: var(--el-fill-color-light);
    color: var(--el-text-color-regular);
    flex-shrink: 0;
    transition: background-color 0.12s ease, color 0.12s ease;
  }

  &__libelle {
    font-size: 13px;
    font-weight: 500;
    color: var(--el-text-color-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
